import { clientApprovalsPath } from '@fos/shared/contracts/approvals';
import type { ClientDto } from '@fos/shared/contracts/crm';
import type { ProjectListItem } from '@fos/shared/contracts/projects';
import { poolTone, poolUsageLine, type ServicePoolUsage } from '@fos/shared/contracts/services';
import { PROJECT_BUDGET_WARNING_RATIO } from '@fos/shared/labels';
import { todayLocal } from '@fos/shared/time';
import { Calendar, Clock, FolderKanban, FolderOpen, Plus, ShieldCheck, Timer, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/cn';
import { formatDateShort, formatDuration, formatTime, formatWeekday, lowerInitial, plural } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import { ContextBanner } from '../../ui/Blocks';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../ui/Menu';
import { SectionHeader } from '../../ui/PageHeader';
import { Section } from '../../ui/Section';
import { Stat } from '../../ui/Stat';
import { EmptyState } from '../../ui/States';
import { ClientBillingStat } from '../billing';
import { ClientSummaryCard, useCopilotStatus } from '../copilot';
import { ClientAssetsSection, PoolBalance, useClientPools } from '../maintenance';
import { TOUCH_TEXT } from '../mobile';
import { ClientProjectsRows, formatHours, ProjectFormDialog, projectHoursText } from '../projects';
import { ClientServicesRows, ServiceFormDialog } from '../services';
import { TaskRows } from '../tasks';

/**
 * Sekcje Przeglądu profilu klienta (Figma 05 Profil REA, 3026:2544), rejestrowane w `composition/index.ts`:
 *  - góra: baner akceptacji, kafelki (główna oferta, do rozliczenia, godziny w miesiącu, najbliższe spotkanie),
 *  - lewa kolumna: „Projekty i usługi”, „Na naszym serwerze”,
 *  - prawa kolumna: „Otwarte zadania” (nad „Kontaktami” modułu klientów).
 * Kafelki Fali 2: główna oferta (oferty + wykorzystanie godzin z wpisów czasu), do rozliczenia (Rozliczenia),
 * godziny w miesiącu (Czas), najbliższe spotkanie (Spotkania) — każdy tylko z dostępem do swojego modułu.
 */

const isArchived = (c: ClientDto) => c.status === 'archived' || c.archivedAt !== null;

const lowerFirst = (s: string) => s.charAt(0).toLocaleLowerCase('pl-PL') + s.slice(1);

// ── Baner akceptacji ────────────────────────────────────────────────────────

/**
 * „1 akcja AI czeka na akceptację: odpowiedź do Anny w sprawie FAQ” + „Przejdź” → lista akcji klienta w Akceptacjach
 * (`/akceptacje?klient=ID`). Liczba i widoczność wg reguł modułu Akceptacje (`today.pendingApprovals`).
 */
export function ClientApprovalsBanner({ client }: { client: ClientDto }) {
  const access = useAccess();
  const enabled = access.can('approvals') && !client.partial;
  const query = trpc.today.pendingApprovals.useQuery({ clientId: client.id, limit: 1 }, { enabled });
  const data = query.data;
  if (!enabled || !data || data.total === 0) return null;
  const n = data.total;
  const first = data.items[0];
  const what = `${n} ${plural(n, ['akcja', 'akcje', 'akcji'])}${data.allByAi ? ' AI' : ''}`;
  const verb = plural(n, ['czeka', 'czekają', 'czeka']);
  return (
    <ContextBanner tone="accent" icon={ShieldCheck} linkTo={clientApprovalsPath(client.id)}>
      {/* mobile (Figma 05 mobile): jedna linia „1 akcja AI do akceptacji” */}
      <span className="md:hidden">{what} do akceptacji</span>
      <span className="hidden md:inline">
        {what} {verb} na akceptację
        {first ? `: ${lowerFirst(first.summary)}` : ''}
        {n > 1 ? ' i inne' : ''}
      </span>
    </ContextBanner>
  );
}

// ── Kafelki ─────────────────────────────────────────────────────────────────

const MONTH_LOCATIVE = [
  'styczniu',
  'lutym',
  'marcu',
  'kwietniu',
  'maju',
  'czerwcu',
  'lipcu',
  'sierpniu',
  'wrześniu',
  'październiku',
  'listopadzie',
  'grudniu',
];

const MONTH_NOMINATIVE = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
];

const monthIndex = (date: string) => Number(date.slice(5, 7)) - 1;

interface HoursBudget {
  name: string;
  used: number;
  budget: number;
  to: string;
}

/**
 * Bez przyjętej oferty: aktywny projekt z budżetem godzin (najbliższy termin). Pakiet godzin i utrzymanie z pulą mają
 * od 17.10.2026 własny kafelek „Pula w …” z prawdziwym wykorzystaniem w miesiącu (`PoolStat`).
 */
function budgetFallback(projects: ProjectListItem[] | undefined): HoursBudget | null {
  const withBudget = (projects ?? [])
    .filter((p) => p.hoursBudgetSeconds && !p.archivedAt && p.status !== 'done' && p.status !== 'cancelled')
    .sort((a, b) => {
      const rank = (p: ProjectListItem) => (p.status === 'active' ? 0 : 1);
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return (a.endDate ?? '9999-12-31').localeCompare(b.endDate ?? '9999-12-31');
    });
  const p = withBudget[0];
  if (p?.hoursBudgetSeconds) {
    return { name: p.name, used: p.usedSeconds, budget: p.hoursBudgetSeconds, to: `/projekty/${p.id}` };
  }
  return null;
}

/**
 * „Główna oferta” (Figma 05: „32 / 40 h”, pasek, „Redesign strony · zostało 8 h”): ostatnio przyjęta oferta
 * klienta (`offers.mainOffer`) z wykorzystaniem godzin projektu o tej samej nazwie (wpisy czasu); bez przyjętej
 * oferty — budżet godzin projektu albo pakiet godzin. Kolor ostrzegawczy od 80% (DECISIONS), czerwony ponad 100%.
 */
function MainOfferStat({ client }: { client: ClientDto }) {
  const access = useAccess();
  const canProjects = access.can('projects');
  // oferty należą do całego klienta (moduł Klienci): w trybie częściowym tylko budżet własnego projektu
  const offersEnabled = !client.partial && access.can('clients');
  const offerQuery = trpc.offers.mainOffer.useQuery({ clientId: client.id }, { enabled: offersEnabled });
  const projects = trpc.projects.listByClient.useQuery(
    { clientId: client.id, includeClosed: false },
    { enabled: canProjects },
  );
  const offer = offerQuery.data ?? null;
  const fromOffer: HoursBudget | null =
    offer?.plannedSeconds && offer.usedSeconds !== null
      ? {
          name: offer.projectName ?? offer.title,
          used: offer.usedSeconds,
          budget: offer.plannedSeconds,
          to: `/oferty/${offer.offerId}`,
        }
      : null;
  const budget = fromOffer ?? budgetFallback(projects.data);
  const pending = (offersEnabled && offerQuery.isPending) || (canProjects && projects.isPending);

  if (!budget) {
    // przyjęta oferta bez projektu o tej nazwie: same godziny z oferty
    const planned = offer?.plannedSeconds ?? null;
    return (
      <Stat
        label="Główna oferta"
        icon={FolderOpen}
        to={offer ? `/oferty/${offer.offerId}` : undefined}
        value={planned ? `${formatHours(planned)} h` : pending ? '…' : '—'}
        showDot={false}
        note={offer ? offer.title : 'brak przyjętej oferty'}
      />
    );
  }
  const ratio = budget.used / budget.budget;
  const tone = ratio > 1 ? 'danger' : ratio >= PROJECT_BUDGET_WARNING_RATIO ? 'warning' : 'neutral';
  const left = budget.budget - budget.used;
  return (
    <Stat
      label="Główna oferta"
      icon={FolderOpen}
      to={budget.to}
      value={projectHoursText(budget.used, budget.budget)}
      progress={Math.min(1, ratio)}
      tone={tone}
      showDot={false}
      note={
        <>
          <span className="hidden md:inline">{budget.name} · </span>
          {left >= 0 ? `zostało ${formatHours(left)} h` : `ponad o ${formatHours(-left)} h`}
        </>
      }
    />
  );
}

/** „Godziny w październiku” (Figma 05: „6 h 40 min”, „wrzesień 38 h”): wszystkie wpisy klienta w zakresie osoby. */
function MonthHoursStat({ client }: { client: ClientDto }) {
  const query = trpc.time.summaryForClient.useQuery({ clientId: client.id });
  const month = MONTH_LOCATIVE[monthIndex(todayLocal())] ?? '';
  const data = query.data;
  return (
    <Stat
      label={`Godziny w ${month}`}
      icon={Clock}
      to={`/czas?widok=raport&klient=${client.id}`}
      value={data ? formatDuration(data.currentMonth.seconds) : query.isError ? '—' : '…'}
      showDot={false}
      note={
        data
          ? `${MONTH_NOMINATIVE[monthIndex(data.previousMonth.from)] ?? ''} ${formatHours(data.previousMonth.seconds)} h`
          : query.isError
            ? 'nie udało się wczytać'
            : 'wczytywanie…'
      }
    />
  );
}

/**
 * „Pula w październiku” (DECISIONS 2026-10-17 [P] „Zgłoszenia z utrzymania liczą się do puli godzin”): wykorzystanie
 * puli aktywnej usługi klienta w bieżącym miesiącu (Europe/Warsaw) — „1 h 20 min z 3 h”, pasek, „zostało 1 h 40 min”;
 * bursztyn od 80%, czerwień ponad 100% z „ponad pulę: X”. Przy kilku usługach z pulą — najstarsza (ta, do której
 * trafiają zgłoszenia z portalu). Same godziny, bez kwot.
 */
function PoolStat({ client, usage }: { client: ClientDto; usage: ServicePoolUsage }) {
  const access = useAccess();
  const tone = poolTone(usage);
  return (
    <Stat
      label={`Pula w ${MONTH_LOCATIVE[monthIndex(usage.from)] ?? ''}`}
      icon={Timer}
      to={access.can('maintenance') ? `/klienci/${client.id}?zakladka=utrzymanie` : undefined}
      fitValue
      value={
        <span title={`${usage.serviceName}: ${poolUsageLine(usage)}`}>
          {formatDuration(usage.usedSeconds)}{' '}
          <span className="text-body-s font-normal text-fg-muted">z {formatDuration(usage.poolSeconds)}</span>
        </span>
      }
      progress={usage.poolSeconds > 0 ? Math.min(1, usage.usedSeconds / usage.poolSeconds) : 0}
      tone={tone}
      showDot={false}
      // stan puli zawsze widoczny (nazwa usługi w podpowiedzi wartości — w wąskim kafelku ucinała „zostało …”)
      note={<PoolBalance usage={usage} />}
    />
  );
}

/** „Najbliższe spotkanie” klienta (Figma 05: „8.10 · 10:00”, „czwartek · przegląd FAQ”). */
function NextMeetingStat({ client }: { client: ClientDto }) {
  const query = trpc.meetings.next.useQuery({ clientId: client.id });
  const m = query.data;
  return (
    <Stat
      label="Najbliższe spotkanie"
      icon={Calendar}
      fitValue
      to={m ? `/spotkania/${m.id}` : undefined}
      value={m ? `${formatDateShort(m.startsAt)} · ${formatTime(m.startsAt)}` : query.isPending ? '…' : '—'}
      showDot={false}
      note={m ? `${formatWeekday(m.startsAt)} · ${lowerInitial(m.title)}` : 'brak zaplanowanych'}
    />
  );
}

export function ClientStatTiles({ client }: { client: ClientDto }) {
  const access = useAccess();
  // pula godzin: cały klient (w trybie częściowym serwer i tak zwraca pustą listę)
  const pools = useClientPools(client.id, !client.partial);
  const pool = pools.data?.[0];
  const tiles = [];
  if (access.can('projects') || access.can('clients')) {
    tiles.push(<MainOfferStat key="offer" client={client} />);
  }
  if (!client.partial && (access.can('time') || access.can('billing'))) {
    tiles.push(<ClientBillingStat key="unbilled" clientId={client.id} />);
  }
  if (pool) tiles.push(<PoolStat key="pool" client={client} usage={pool} />);
  if (!client.partial && access.can('time')) {
    tiles.push(<MonthHoursStat key="hours" client={client} />);
  }
  if (!client.partial && access.can('clients')) {
    tiles.push(<NextMeetingStat key="meeting" client={client} />);
  }
  if (tiles.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
      {tiles.map((t) => (
        <div
          key={t.key}
          className={cn(
            'min-w-0 [&>*]:h-full',
            // mobile: bez „Godzin w miesiącu”, spotkanie i pula na pełną szerokość (Figma 05 mobile)
            t.key === 'hours' && 'hidden md:block',
            (t.key === 'meeting' || t.key === 'pool') && 'col-span-2 md:col-span-1',
          )}
        >
          {t}
        </div>
      ))}
    </div>
  );
}

/** Góra Przeglądu: baner i kafelki (jedna sekcja, żeby odstępy były równe). */
export function ClientOverviewTop({ client }: { client: ClientDto }) {
  return (
    <div className="flex flex-col gap-4">
      <ClientApprovalsBanner client={client} />
      <ClientStatTiles client={client} />
    </div>
  );
}

// ── Projekty i usługi ───────────────────────────────────────────────────────

export function ClientProjectsAndServices({ client }: { client: ClientDto }) {
  const access = useAccess();
  const canProjects = access.can('projects');
  const canServices = canProjects || access.can('maintenance');
  const [dialog, setDialog] = useState<'project' | 'service' | null>(null);
  const projects = trpc.projects.listByClient.useQuery(
    { clientId: client.id, includeClosed: false },
    { enabled: canProjects },
  );
  const services = trpc.services.listByClient.useQuery(
    { clientId: client.id, includeArchived: false },
    { enabled: canServices && !client.partial },
  );
  if (!canServices) return null;

  const archived = isArchived(client);
  const canAddProject = canProjects && access.can('projects', 'edit') && client.canEdit && !client.partial && !archived;
  const canAddService =
    (access.can('maintenance', 'edit') || access.can('projects', 'edit')) &&
    client.canEdit &&
    !client.partial &&
    !archived;
  const projectsEmpty = !canProjects || (projects.isSuccess && projects.data.length === 0);
  const servicesEmpty = client.partial || (services.isSuccess && services.data.length === 0);

  const addLink =
    canAddProject || canAddService ? (
      <Menu>
        <MenuTrigger
          className={`rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          Dodaj
        </MenuTrigger>
        <MenuContent align="end" className="w-48">
          {canAddProject && (
            <MenuItem icon={FolderKanban} onSelect={() => setDialog('project')}>
              Projekt
            </MenuItem>
          )}
          {canAddService && (
            <MenuItem icon={Wrench} onSelect={() => setDialog('service')}>
              Usługa albo umowa
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
    ) : undefined;

  return (
    <Section aria-labelledby="client-projects-services">
      <SectionHeader
        tone="strong"
        title={<span id="client-projects-services">Projekty i usługi</span>}
        action={addLink}
      />
      <ClientProjectsRows clientId={client.id} showEmpty={false} />
      {!client.partial && <ClientServicesRows clientId={client.id} />}
      {projectsEmpty && servicesEmpty && (
        <EmptyState
          compact
          icon={FolderKanban}
          title="Brak projektów i usług"
          action={
            canAddProject ? (
              <button
                type="button"
                onClick={() => setDialog('project')}
                className={`inline-flex items-center gap-1 rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
              >
                <Plus size={14} strokeWidth={1.75} aria-hidden />
                Dodaj projekt
              </button>
            ) : undefined
          }
        />
      )}
      {canAddProject && (
        <ProjectFormDialog
          open={dialog === 'project'}
          onOpenChange={(o) => !o && setDialog(null)}
          defaultClientId={client.id}
        />
      )}
      {canAddService && (
        <ServiceFormDialog
          open={dialog === 'service'}
          onOpenChange={(o) => !o && setDialog(null)}
          clientId={client.id}
          defaultKind={access.can('maintenance', 'edit') ? 'maintenance' : 'consulting'}
        />
      )}
    </Section>
  );
}

// ── Na naszym serwerze ──────────────────────────────────────────────────────

export function ClientServerSection({ client }: { client: ClientDto }) {
  return <ClientAssetsSection clientId={client.id} />;
}

// ── W skrócie (Fala 4) ──────────────────────────────────────────────────────

/**
 * „W skrócie” (Figma 05, lewa kolumna pod „Na naszym serwerze”): podsumowanie klienta od AI z podpisem
 * „Podsumowanie AI, …” (w symulacji „· Symulacja”) i „Odśwież”. Przy wyłączonym AI sekcji nie ma (profil działa bez
 * zmian); klient w trybie częściowym — bez podsumowania (kontekst to cały klient).
 */
export function ClientAiSummary({ client }: { client: ClientDto }) {
  const status = useCopilotStatus(!client.partial);
  if (client.partial || !status.data?.enabled) return null;
  return <ClientSummaryCard clientId={client.id} />;
}

// ── Otwarte zadania ─────────────────────────────────────────────────────────

export function ClientOpenTasks({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.can('tasks')) return null;
  const open = client.stats.openTasks ?? 0;
  return (
    <Section aria-labelledby="client-open-tasks">
      <SectionHeader
        tone="strong"
        title={<span id="client-open-tasks">Otwarte zadania</span>}
        action={
          open > 0 ? (
            <Link
              to={`/zadania?widok=lista&klient=${client.id}`}
              className={`rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
            >
              Wszystkie ({open})
            </Link>
          ) : undefined
        }
      />
      <TaskRows filter={{ clientId: client.id }} limit={5} allowAdd={!client.partial && !isArchived(client)} />
    </Section>
  );
}
